import React, { useEffect, useState, Component } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Printer, MessageCircle, RefreshCw, FileText, CheckCircle2, Save, Loader2, List, ChevronDown, ChevronUp } from 'lucide-react';
import { formatCurrency } from '../../lib/utils';
import { supabase } from '../../lib/supabase';
const SERVICE_TYPES = ['Menú Digital Fase 1', 'Menú Digital Fase 2', 'Menú Digital Completo', 'Landing Page', 'KDS Sistema Cocina', 'Delivery System', 'Personalizado'];
const PAYMENT_METHODS = ['SINPE Móvil', 'Transferencia Bancaria', 'Efectivo', 'Otro'];
const PAYMENT_TYPES = ['Pago Total', 'Primer Abono (50%)', 'Segundo Abono (50%)', 'Abono Parcial'];
const DEFAULT_DESCRIPTIONS: Record<string, string> = {
  'Menú Digital Fase 1': 'Implementación de Menú Digital Fase 1. Incluye configuración inicial, carga de menú, y código QR.',
  'Menú Digital Fase 2': 'Implementación de Menú Digital Fase 2. Incluye sistema de pedidos, integración con KDS y reportes básicos.',
  'Menú Digital Completo': 'Sistema integral de Menú Digital. Incluye pedidos, KDS, reportes avanzados y módulo de lealtad.',
  'Landing Page': 'Diseño y desarrollo de Landing Page personalizada con integración al menú digital.',
  'KDS Sistema Cocina': 'Implementación de Kitchen Display System (KDS) para gestión de comandas en cocina.',
  'Delivery System': 'Módulo completo de delivery con asignación de choferes y tracking de órdenes.',
  Personalizado: 'Servicios de desarrollo y configuración personalizados.'
};
export function ReceiptGeneratorPage() {
  const navigate = useNavigate();
  const getNextReceiptNumber = async (): Promise<string> => {
    const year = new Date().getFullYear();
    const prefix = `NOIZA-${year}-`;
    try {
      const {
        data
      } = await supabase.from('noiza_receipts').select('receipt_number').like('receipt_number', `${prefix}%`).order('receipt_number', {
        ascending: false
      }).limit(1);
      if (data && data.length > 0) {
        const lastNum = parseInt(data[0].receipt_number.replace(prefix, ''), 10);
        return `${prefix}${String((lastNum || 0) + 1).padStart(4, '0')}`;
      }
      return `${prefix}0001`;
    } catch {
      return `${prefix}0001`;
    }
  };
  const [formData, setFormData] = useState({
    receiptNumber: '',
    date: new Date().toISOString().split('T')[0],
    clientName: '',
    clientId: '',
    clientPhone: '',
    serviceType: 'Menú Digital Fase 1',
    serviceDescription: DEFAULT_DESCRIPTIONS['Menú Digital Fase 1'],
    totalAmount: 100000,
    paymentAmount: 50000,
    paymentMethod: 'SINPE Móvil',
    paymentReference: '',
    paymentType: 'Primer Abono (50%)',
    notes: ''
  });
  const [savedReceipts, setSavedReceipts] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [showSavedList, setShowSavedList] = useState(false);
  const [loadingReceipts, setLoadingReceipts] = useState(false);
  const remainingBalance = formData.totalAmount - formData.paymentAmount;
  useEffect(() => {
    fetchReceipts();
    // Set initial sequential receipt number
    getNextReceiptNumber().then(num => {
      setFormData(prev => ({
        ...prev,
        receiptNumber: num
      }));
    });
  }, []);
  const fetchReceipts = async () => {
    setLoadingReceipts(true);
    try {
      const {
        data,
        error
      } = await supabase.from('noiza_receipts').select('*').order('created_at', {
        ascending: false
      }).limit(20);
      if (error) throw error;
      if (data) setSavedReceipts(data);
    } catch (err) {
      console.error('Error fetching receipts:', err);
    } finally {
      setLoadingReceipts(false);
    }
  };
  const handleSave = async () => {
    if (!formData.clientName) {
      alert('Por favor ingresa el nombre del cliente');
      return;
    }
    setSaving(true);
    try {
      // Check if receipt number already exists
      const {
        data: existing
      } = await supabase.from('noiza_receipts').select('id').eq('receipt_number', formData.receiptNumber).single();
      const payload = {
        receipt_number: formData.receiptNumber,
        receipt_date: formData.date,
        client_name: formData.clientName,
        client_id: formData.clientId,
        client_phone: formData.clientPhone,
        service_type: formData.serviceType,
        service_description: formData.serviceDescription,
        total_amount: formData.totalAmount,
        payment_amount: formData.paymentAmount,
        remaining_balance: remainingBalance,
        payment_method: formData.paymentMethod,
        payment_reference: formData.paymentReference,
        payment_type: formData.paymentType,
        notes: formData.notes
      };
      let error;
      if (existing) {
        // Update
        const {
          error: updateError
        } = await supabase.from('noiza_receipts').update(payload).eq('id', existing.id);
        error = updateError;
      } else {
        // Insert
        const {
          error: insertError
        } = await supabase.from('noiza_receipts').insert([payload]);
        error = insertError;
      }
      if (error) throw error;
      setSaveSuccess(true);
      fetchReceipts();
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving receipt:', err);
      alert('Error al guardar el recibo. Revisa la consola para más detalles.');
    } finally {
      setSaving(false);
    }
  };
  const loadReceipt = (receipt: any) => {
    setFormData({
      receiptNumber: receipt.receipt_number,
      date: receipt.receipt_date,
      clientName: receipt.client_name,
      clientId: receipt.client_id || '',
      clientPhone: receipt.client_phone || '',
      serviceType: receipt.service_type,
      serviceDescription: receipt.service_description || '',
      totalAmount: receipt.total_amount,
      paymentAmount: receipt.payment_amount,
      paymentMethod: receipt.payment_method,
      paymentReference: receipt.payment_reference || '',
      paymentType: receipt.payment_type,
      notes: receipt.notes || ''
    });
    // Scroll to top
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  const handleServiceTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newType = e.target.value;
    setFormData(prev => ({
      ...prev,
      serviceType: newType,
      serviceDescription: DEFAULT_DESCRIPTIONS[newType] || ''
    }));
  };
  const handleReset = async () => {
    const nextNum = await getNextReceiptNumber();
    setFormData({
      receiptNumber: nextNum,
      date: new Date().toISOString().split('T')[0],
      clientName: '',
      clientId: '',
      clientPhone: '',
      serviceType: 'Menú Digital Fase 1',
      serviceDescription: DEFAULT_DESCRIPTIONS['Menú Digital Fase 1'],
      totalAmount: 100000,
      paymentAmount: 50000,
      paymentMethod: 'SINPE Móvil',
      paymentReference: '',
      paymentType: 'Primer Abono (50%)',
      notes: ''
    });
  };
  const handlePrint = () => {
    window.print();
  };
  const handleWhatsAppShare = () => {
    const text = `*RECIBO DE PAGO - NOIZA* 🧾

*Recibo N°:* ${formData.receiptNumber}
*Fecha:* ${formData.date}
*Cliente:* ${formData.clientName}

*Servicio:* ${formData.serviceType}
*Tipo de Pago:* ${formData.paymentType}
*Método:* ${formData.paymentMethod} ${formData.paymentReference ? `(Ref: ${formData.paymentReference})` : ''}

*Monto Pagado:* ${formatCurrency(formData.paymentAmount)}
*Saldo Pendiente:* ${formatCurrency(remainingBalance)}

Gracias por confiar en NOIZA.
Plataforma de Delivery Digital - Costa Rica
noizacr.com`;
    const encodedText = encodeURIComponent(text);
    let phone = formData.clientPhone.replace(/\D/g, '');
    if (phone && !phone.startsWith('506')) {
      phone = '506' + phone;
    }
    const url = phone ? `https://wa.me/${phone}?text=${encodedText}` : `https://wa.me/?text=${encodedText}`;
    window.open(url, '_blank');
  };
  return <div className="min-h-screen bg-[#0B0B0B] text-white print:bg-white print:text-black" data-id="element-4004">
      {/* Print Styles */}
      <style data-id="element-4005">
        {`
          @media print {
            * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
            html, body { background: white !important; margin: 0 !important; padding: 0 !important; }
            .no-print { display: none !important; }
            .print-only { display: block !important; }
            .receipt-container {
              box-shadow: none !important;
              border: none !important;
              padding: 24px !important;
              margin: 0 auto !important;
              width: 100% !important;
              max-width: 100% !important;
              border-radius: 0 !important;
              background: white !important;
              color: black !important;
            }
            .print-text-black { color: black !important; }
            .print-border-black { border-color: #e5e7eb !important; }
            .print-bg-gray { background-color: #f9fafb !important; }
          }
        `}
      </style>

      {/* Header - Hidden in print */}
      <div className="no-print sticky top-0 z-10 bg-[#0B0B0B]/95 backdrop-blur-md border-b border-white/10 px-4 sm:px-6 py-4" data-id="element-4006">
        <div className="max-w-7xl mx-auto flex items-center justify-between" data-id="element-4007">
          <div className="flex items-center gap-4" data-id="element-4008">
            <button onClick={() => navigate('/admin/enrollment')} className="p-2 hover:bg-white/5 rounded-lg transition-colors" data-id="element-4009">
              <ArrowLeft className="w-5 h-5 text-white/70" data-id="element-4010" />
            </button>
            <div data-id="element-4011">
              <h1 className="text-xl font-bold text-white" data-id="element-4012">
                Generador de Recibos
              </h1>
              <p className="text-sm text-white/50" data-id="element-4013">NOIZA Admin</p>
            </div>
          </div>
          <div className="flex items-center gap-3" data-id="element-4014">
            <button onClick={handleReset} className="px-4 py-2 bg-white/5 hover:bg-white/10 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2" data-id="element-4015">
              <RefreshCw className="w-4 h-4" data-id="element-4016" />
              <span className="hidden sm:inline" data-id="element-4017">Nuevo</span>
            </button>

            <button onClick={handleSave} disabled={saving} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${saveSuccess ? 'bg-emerald-500/20 text-emerald-400' : 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30'}`} data-id="element-4018">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" data-id="element-4019" /> : saveSuccess ? <CheckCircle2 className="w-4 h-4" data-id="element-4020" /> : <Save className="w-4 h-4" data-id="element-4021" />}
              <span className="hidden sm:inline" data-id="element-4022">
                {saving ? 'Guardando...' : saveSuccess ? 'Guardado' : 'Guardar'}
              </span>
            </button>

            <button onClick={handleWhatsAppShare} className="px-4 py-2 bg-[#25D366]/20 text-[#25D366] hover:bg-[#25D366]/30 rounded-lg text-sm font-medium transition-colors flex items-center gap-2" data-id="element-4023">
              <MessageCircle className="w-4 h-4" data-id="element-4024" />
              <span className="hidden sm:inline" data-id="element-4025">WhatsApp</span>
            </button>
            <button onClick={handlePrint} className="px-4 py-2 bg-[#C9A34E] text-[#0B0B0B] hover:bg-[#C9A34E]/90 rounded-lg text-sm font-bold transition-colors flex items-center gap-2" data-id="element-4026">
              <Printer className="w-4 h-4" data-id="element-4027" />
              <span className="hidden sm:inline" data-id="element-4028">Imprimir / PDF</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8" data-id="element-4029">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-id="element-4030">
          {/* Form Section */}
          <div className="lg:col-span-5 space-y-6 no-print" data-id="element-4031">
            <div className="bg-[#141414] border border-white/10 rounded-2xl p-6" data-id="element-4032">
              <h2 className="text-lg font-bold text-white mb-6 flex items-center gap-2" data-id="element-4033">
                <FileText className="w-5 h-5 text-[#C9A34E]" data-id="element-4034" />
                Datos del Recibo
              </h2>

              <div className="space-y-5" data-id="element-4035">
                <div className="grid grid-cols-2 gap-4" data-id="element-4036">
                  <div data-id="element-4037">
                    <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-4038">
                      N° de Recibo
                    </label>
                    <input type="text" value={formData.receiptNumber} onChange={e => setFormData({
                    ...formData,
                    receiptNumber: e.target.value
                  })} className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-4039" />
                  </div>
                  <div data-id="element-4040">
                    <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-4041">
                      Fecha
                    </label>
                    <input type="date" value={formData.date} onChange={e => setFormData({
                    ...formData,
                    date: e.target.value
                  })} className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-4042" />
                  </div>
                </div>

                <div className="space-y-4" data-id="element-4043">
                  <h3 className="text-sm font-semibold text-white/80 border-b border-white/10 pb-2" data-id="element-4044">
                    Cliente
                  </h3>
                  <div data-id="element-4045">
                    <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-4046">
                      Nombre / Razón Social
                    </label>
                    <input type="text" value={formData.clientName} onChange={e => setFormData({
                    ...formData,
                    clientName: e.target.value
                  })} placeholder="Ej. Restaurante El Sabor" className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-4047" />
                  </div>
                  <div className="grid grid-cols-2 gap-4" data-id="element-4048">
                    <div data-id="element-4049">
                      <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-4050">
                        Cédula (Opcional)
                      </label>
                      <input type="text" value={formData.clientId} onChange={e => setFormData({
                      ...formData,
                      clientId: e.target.value
                    })} className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-4051" />
                    </div>
                    <div data-id="element-4052">
                      <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-4053">
                        Teléfono / WhatsApp
                      </label>
                      <input type="text" value={formData.clientPhone} onChange={e => setFormData({
                      ...formData,
                      clientPhone: e.target.value
                    })} className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-4054" />
                    </div>
                  </div>
                </div>

                <div className="space-y-4" data-id="element-4055">
                  <h3 className="text-sm font-semibold text-white/80 border-b border-white/10 pb-2" data-id="element-4056">
                    Servicio
                  </h3>
                  <div data-id="element-4057">
                    <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-4058">
                      Tipo de Servicio
                    </label>
                    <select value={formData.serviceType} onChange={handleServiceTypeChange} className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-4059">
                      {SERVICE_TYPES.map(type => <option key={type} value={type} data-id="element-4060">
                          {type}
                        </option>)}
                    </select>
                  </div>
                  <div data-id="element-4061">
                    <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-4062">
                      Descripción
                    </label>
                    <textarea value={formData.serviceDescription} onChange={e => setFormData({
                    ...formData,
                    serviceDescription: e.target.value
                  })} rows={3} className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E] resize-none" data-id="element-4063" />
                  </div>
                </div>

                <div className="space-y-4" data-id="element-4064">
                  <h3 className="text-sm font-semibold text-white/80 border-b border-white/10 pb-2" data-id="element-4065">
                    Pago
                  </h3>
                  <div className="grid grid-cols-2 gap-4" data-id="element-4066">
                    <div data-id="element-4067">
                      <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-4068">
                        Monto Total (Contrato)
                      </label>
                      <div className="relative" data-id="element-4069">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" data-id="element-4070">
                          ₡
                        </span>
                        <input type="number" value={formData.totalAmount} onChange={e => setFormData({
                        ...formData,
                        totalAmount: Number(e.target.value)
                      })} className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg pl-8 pr-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-4071" />
                      </div>
                    </div>
                    <div data-id="element-4072">
                      <label className="block text-xs font-medium text-[#C9A34E] mb-1.5" data-id="element-4073">
                        Monto de este Pago
                      </label>
                      <div className="relative" data-id="element-4074">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#C9A34E]/60" data-id="element-4075">
                          ₡
                        </span>
                        <input type="number" value={formData.paymentAmount} onChange={e => setFormData({
                        ...formData,
                        paymentAmount: Number(e.target.value)
                      })} className="w-full bg-[#C9A34E]/10 border border-[#C9A34E]/30 rounded-lg pl-8 pr-3 py-2 text-[#C9A34E] font-bold text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-4076" />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4" data-id="element-4077">
                    <div data-id="element-4078">
                      <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-4079">
                        Tipo de Pago
                      </label>
                      <select value={formData.paymentType} onChange={e => setFormData({
                      ...formData,
                      paymentType: e.target.value
                    })} className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-4080">
                        {PAYMENT_TYPES.map(type => <option key={type} value={type} data-id="element-4081">
                            {type}
                          </option>)}
                      </select>
                    </div>
                    <div data-id="element-4082">
                      <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-4083">
                        Método de Pago
                      </label>
                      <select value={formData.paymentMethod} onChange={e => setFormData({
                      ...formData,
                      paymentMethod: e.target.value
                    })} className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-4084">
                        {PAYMENT_METHODS.map(method => <option key={method} value={method} data-id="element-4085">
                            {method}
                          </option>)}
                      </select>
                    </div>
                  </div>

                  <div data-id="element-4086">
                    <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-4087">
                      Referencia (Opcional)
                    </label>
                    <input type="text" value={formData.paymentReference} onChange={e => setFormData({
                    ...formData,
                    paymentReference: e.target.value
                  })} placeholder="Ej. Comprobante #123456" className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E]" data-id="element-4088" />
                  </div>

                  <div data-id="element-4089">
                    <label className="block text-xs font-medium text-white/60 mb-1.5" data-id="element-4090">
                      Notas Adicionales
                    </label>
                    <textarea value={formData.notes} onChange={e => setFormData({
                    ...formData,
                    notes: e.target.value
                  })} rows={2} className="w-full bg-[#0B0B0B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#C9A34E] resize-none" data-id="element-4091" />
                  </div>
                </div>
              </div>
            </div>

            {/* Saved Receipts Panel */}
            <div className="bg-[#141414] border border-white/10 rounded-2xl overflow-hidden" data-id="element-4092">
              <button onClick={() => setShowSavedList(!showSavedList)} className="w-full flex items-center justify-between p-6 hover:bg-white/5 transition-colors" data-id="element-4093">
                <h2 className="text-lg font-bold text-white flex items-center gap-2" data-id="element-4094">
                  <List className="w-5 h-5 text-blue-400" data-id="element-4095" />
                  Recibos Guardados
                  <span className="bg-white/10 text-white/70 text-xs py-0.5 px-2 rounded-full ml-2" data-id="element-4096">
                    {savedReceipts.length}
                  </span>
                </h2>
                {showSavedList ? <ChevronUp className="w-5 h-5 text-white/50" data-id="element-4097" /> : <ChevronDown className="w-5 h-5 text-white/50" data-id="element-4098" />}
              </button>

              {showSavedList && <div className="border-t border-white/10 p-4 max-h-96 overflow-y-auto custom-scrollbar" data-id="element-4099">
                  {loadingReceipts ? <div className="flex items-center justify-center py-8" data-id="element-4100">
                      <Loader2 className="w-6 h-6 text-white/30 animate-spin" data-id="element-4101" />
                    </div> : savedReceipts.length === 0 ? <div className="text-center py-8 text-white/40 text-sm" data-id="element-4102">
                      No hay recibos guardados aún.
                    </div> : <div className="space-y-2" data-id="element-4103">
                      {savedReceipts.map(receipt => <button key={receipt.id} onClick={() => loadReceipt(receipt)} className="w-full text-left bg-[#0B0B0B] hover:bg-white/5 border border-white/5 rounded-xl p-4 transition-colors group flex items-center justify-between" data-id="element-4104">
                          <div data-id="element-4105">
                            <div className="flex items-center gap-2 mb-1" data-id="element-4106">
                              <span className="text-sm font-bold text-white" data-id="element-4107">
                                {receipt.receipt_number}
                              </span>
                              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-md bg-white/10 text-white/70" data-id="element-4108">
                                {receipt.payment_type}
                              </span>
                            </div>
                            <p className="text-sm text-white/70 font-medium" data-id="element-4109">
                              {receipt.client_name}
                            </p>
                            <p className="text-xs text-white/40 mt-1" data-id="element-4110">
                              {new Date(receipt.receipt_date).toLocaleDateString('es-CR')}{' '}
                              • {receipt.service_type}
                            </p>
                          </div>
                          <div className="text-right" data-id="element-4111">
                            <p className="text-sm font-bold text-[#C9A34E]" data-id="element-4112">
                              {formatCurrency(receipt.payment_amount)}
                            </p>
                            <p className="text-xs text-white/40 mt-1 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-4113">
                              Ver recibo →
                            </p>
                          </div>
                        </button>)}
                    </div>}
                </div>}
            </div>
          </div>

          {/* Preview Section */}
          <div className="lg:col-span-7 print:col-span-12" data-id="element-4114">
            <div className="sticky top-24 print:static" data-id="element-4115">
              <h2 className="text-sm font-semibold text-white/60 mb-4 uppercase tracking-wider no-print" data-id="element-4116">
                Vista Previa del Recibo
              </h2>

              {/* THE RECEIPT */}
              <div className="receipt-container bg-white text-black rounded-xl p-8 sm:p-12 shadow-2xl shadow-black/50 max-w-2xl mx-auto border border-white/20 print:shadow-none print:border-none print:p-0 print:max-w-full print:rounded-none" data-id="element-4117">
                {/* Header */}
                <div className="flex justify-between items-start mb-10" data-id="element-4118">
                  <div data-id="element-4119">
                    <div className="flex items-center gap-2 mb-2" data-id="element-4120">
                      <div className="w-10 h-10 bg-[#0B0B0B] rounded-lg flex items-center justify-center print-bg-gray" data-id="element-4121">
                        <span className="text-[#C9A34E] font-bold text-2xl leading-none" data-id="element-4122">
                          N
                        </span>
                      </div>
                      <span className="font-bold text-3xl tracking-tight text-[#0B0B0B] print-text-black" style={{
                      fontFamily: "'Playfair Display', serif"
                    }} data-id="element-4123">
                        NOIZA
                      </span>
                    </div>
                    <p className="text-sm text-gray-500" data-id="element-4124">
                      Plataforma de Delivery Digital
                    </p>
                    <p className="text-sm text-gray-500" data-id="element-4125">Costa Rica</p>
                    <p className="text-sm text-gray-500" data-id="element-4126">noizacr.com</p>
                  </div>
                  <div className="text-right" data-id="element-4127">
                    <h2 className="text-3xl font-black text-gray-200 mb-2 uppercase tracking-widest" data-id="element-4128">
                      Recibo
                    </h2>
                    <p className="text-sm font-bold text-gray-800" data-id="element-4129">
                      N° {formData.receiptNumber}
                    </p>
                    <p className="text-sm text-gray-500" data-id="element-4130">
                      Fecha:{' '}
                      {new Date(formData.date).toLocaleDateString('es-CR')}
                    </p>
                  </div>
                </div>

                {/* Client Info */}
                <div className="mb-8 p-4 bg-gray-50 rounded-lg border border-gray-100 print-bg-gray print-border-black" data-id="element-4131">
                  <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2" data-id="element-4132">
                    Recibí de:
                  </h3>
                  <p className="text-lg font-bold text-gray-800" data-id="element-4133">
                    {formData.clientName || 'Nombre del Cliente'}
                  </p>
                  <div className="flex gap-6 mt-1 text-sm text-gray-600" data-id="element-4134">
                    {formData.clientId && <p data-id="element-4135">
                        <span className="font-semibold" data-id="element-4136">Cédula:</span>{' '}
                        {formData.clientId}
                      </p>}
                    {formData.clientPhone && <p data-id="element-4137">
                        <span className="font-semibold" data-id="element-4138">Tel/WA:</span>{' '}
                        {formData.clientPhone}
                      </p>}
                  </div>
                </div>

                {/* Service Details */}
                <div className="mb-8" data-id="element-4139">
                  <table className="w-full text-left border-collapse" data-id="element-4140">
                    <thead data-id="element-4141">
                      <tr className="border-b-2 border-gray-200" data-id="element-4142">
                        <th className="py-3 text-xs font-bold text-gray-400 uppercase tracking-wider" data-id="element-4143">
                          Descripción del Servicio
                        </th>
                        <th className="py-3 text-xs font-bold text-gray-400 uppercase tracking-wider text-right" data-id="element-4144">
                          Total Contrato
                        </th>
                      </tr>
                    </thead>
                    <tbody data-id="element-4145">
                      <tr className="border-b border-gray-100" data-id="element-4146">
                        <td className="py-4 pr-4" data-id="element-4147">
                          <p className="font-bold text-gray-800 mb-1" data-id="element-4148">
                            {formData.serviceType}
                          </p>
                          <p className="text-sm text-gray-500" data-id="element-4149">
                            {formData.serviceDescription}
                          </p>
                        </td>
                        <td className="py-4 text-right font-semibold text-gray-800 align-top" data-id="element-4150">
                          {formatCurrency(formData.totalAmount)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Payment Breakdown */}
                <div className="flex justify-end mb-10" data-id="element-4151">
                  <div className="w-full sm:w-1/2 space-y-3" data-id="element-4152">
                    <div className="flex justify-between text-sm text-gray-600" data-id="element-4153">
                      <span data-id="element-4154">Total del Contrato:</span>
                      <span data-id="element-4155">{formatCurrency(formData.totalAmount)}</span>
                    </div>
                    <div className="flex justify-between text-lg font-bold text-[#0B0B0B] border-y border-gray-200 py-3 print-text-black" data-id="element-4156">
                      <span data-id="element-4157">Monto Pagado:</span>
                      <span data-id="element-4158">{formatCurrency(formData.paymentAmount)}</span>
                    </div>
                    <div className="flex justify-between text-sm font-medium text-gray-500" data-id="element-4159">
                      <span data-id="element-4160">Saldo Pendiente:</span>
                      <span data-id="element-4161">{formatCurrency(remainingBalance)}</span>
                    </div>
                  </div>
                </div>

                {/* Payment Info & Notes */}
                <div className="grid grid-cols-2 gap-8 mb-12" data-id="element-4162">
                  <div data-id="element-4163">
                    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2" data-id="element-4164">
                      Detalles del Pago
                    </h3>
                    <p className="text-sm text-gray-800" data-id="element-4165">
                      <span className="font-semibold" data-id="element-4166">Tipo:</span>{' '}
                      {formData.paymentType}
                    </p>
                    <p className="text-sm text-gray-800" data-id="element-4167">
                      <span className="font-semibold" data-id="element-4168">Método:</span>{' '}
                      {formData.paymentMethod}
                    </p>
                    {formData.paymentReference && <p className="text-sm text-gray-800" data-id="element-4169">
                        <span className="font-semibold" data-id="element-4170">Referencia:</span>{' '}
                        {formData.paymentReference}
                      </p>}
                  </div>
                  {formData.notes && <div data-id="element-4171">
                      <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2" data-id="element-4172">
                        Notas
                      </h3>
                      <p className="text-sm text-gray-600 whitespace-pre-wrap" data-id="element-4173">
                        {formData.notes}
                      </p>
                    </div>}
                </div>

                {/* Footer */}
                <div className="border-t border-gray-200 pt-6 flex justify-between items-end relative" data-id="element-4174">
                  <div data-id="element-4175">
                    <p className="text-xs text-gray-400 font-medium" data-id="element-4176">
                      Este recibo es un comprobante de pago válido.
                    </p>
                    <p className="text-xs text-gray-400 font-medium mt-1" data-id="element-4177">
                      Generado por NOIZA - Plataforma de Delivery Digital
                    </p>
                  </div>

                  {/* Watermark/Stamp */}
                  <div className="absolute right-0 bottom-4 opacity-10 rotate-[-15deg] pointer-events-none" data-id="element-4178">
                    <div className="border-4 border-black rounded-lg px-6 py-2" data-id="element-4179">
                      <span className="text-4xl font-black tracking-widest uppercase" data-id="element-4180">
                        {formData.paymentAmount >= formData.totalAmount ? 'Pagado' : 'Abono'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}